/**
 * <InviteBuddyModal> — pick a colleague, send them a peer-duel invite.
 *
 * Shown from any DeckRow (StudyHub → 🥊 Colleague button). Search is
 * debounced against /api/study/buddies/search which returns tenant
 * users other than self. On invite success we route the host straight
 * into /study/duel?duel=<id> so they can watch the "waiting for
 * partner" state; the partner picks it up in their /study/duels inbox.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STUDY_THEME } from './studyTheme';
import { searchBuddies, invitePeerDuel } from '../../lib/studyApi';

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 200, padding: 20,
};

const dialog = {
  background: 'white', width: '100%', maxWidth: 520,
  borderRadius: 16, padding: 28, boxShadow: '0 30px 60px rgba(0,0,0,0.35)',
  fontFamily: 'inherit',
};

const label = {
  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 8,
};

export default function InviteBuddyModal({ deck, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState(null);   // selected buddy row
  const [bestOf, setBestOf] = useState(10);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const debounceRef = useRef(null);

  // Load initial roster (empty q returns first 12).
  useEffect(() => {
    searchBuddies('')
      .then((r) => setCandidates(r.buddies || []))
      .catch(() => setCandidates([]));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchBuddies(q)
        .then((r) => setCandidates(r.buddies || []))
        .catch(() => {});
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const canSend = Boolean(picked && !busy);

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setErr('');
    try {
      const duel = await invitePeerDuel(deck.id, picked.email, bestOf);
      // Route host directly to duel view — will be in "waiting" state.
      navigate(`/study/duel?duel=${duel.id}`);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Could not send invite');
      setBusy(false);
    }
  };

  const roleColor = useMemo(() => ({
    student: STUDY_THEME.muted,
    teacher: STUDY_THEME.goldDeep,
    developer: '#B45309',
    institution_admin: '#7C3AED',
  }), []);

  return (
    <div style={backdrop} onClick={onClose} data-testid="invite-buddy-modal">
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <div style={label}>Challenge a Colleague</div>
        <h2 style={{
          fontFamily: '"Playfair Display", serif',
          margin: '2px 0 4px', fontSize: 24,
        }}>{deck?.name}</h2>
        <p style={{
          fontSize: 13, color: STUDY_THEME.inkSoft, margin: '0 0 18px',
        }}>
          Pick a buddy to duel on this deck. You&apos;ll both see the same
          cards; whoever answers correctly and fastest wins each round.
        </p>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="buddy-search" style={{
            display: 'block', fontSize: 12, fontWeight: 600,
            color: STUDY_THEME.inkSoft, marginBottom: 4,
          }}>Search by name or email</label>
          <input
            id="buddy-search"
            data-testid="buddy-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null); }}
            placeholder="Start typing…"
            style={{
              width: '100%', padding: '10px 12px', fontSize: 14,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 8,
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>

        <div
          data-testid="buddy-list"
          style={{
            maxHeight: 220, overflowY: 'auto', marginBottom: 16,
            border: `1px solid ${STUDY_THEME.line}`, borderRadius: 8,
          }}
        >
          {candidates.length === 0 && (
            <div style={{
              padding: 20, textAlign: 'center', fontSize: 13,
              color: STUDY_THEME.muted,
            }}>
              No colleagues match &ldquo;{q}&rdquo;.
            </div>
          )}
          {candidates.map((b) => {
            const isPicked = picked && picked.email === b.email;
            return (
              <button
                key={b.email}
                onClick={() => setPicked(b)}
                data-testid={`buddy-option-${b.email}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', textAlign: 'left', padding: '10px 14px',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: isPicked ? 'rgba(252,211,89,0.28)' : 'white',
                  borderBottom: `1px solid ${STUDY_THEME.line}`,
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: roleColor[b.role] || STUDY_THEME.muted,
                  color: 'white', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 700, fontSize: 14,
                  flexShrink: 0,
                }}>
                  {(b.name || b.email).slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 14, color: STUDY_THEME.ink,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{b.name || b.email}</div>
                  <div style={{
                    fontSize: 12, color: STUDY_THEME.muted,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{b.email} · {b.role}</div>
                </div>
                {isPicked && (
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 1,
                    color: STUDY_THEME.goldDeep, textTransform: 'uppercase',
                  }}>Selected</div>
                )}
              </button>
            );
          })}
        </div>

        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16,
        }}>
          <label htmlFor="best-of" style={{
            fontSize: 13, color: STUDY_THEME.inkSoft, fontWeight: 600,
          }}>Best of</label>
          <select
            id="best-of"
            data-testid="best-of-select"
            value={bestOf}
            onChange={(e) => setBestOf(Number(e.target.value))}
            style={{
              padding: '6px 10px', fontSize: 13,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              fontFamily: 'inherit',
            }}
          >
            {[3, 5, 10, 15].map((n) => (
              <option key={n} value={n}>{n} cards</option>
            ))}
          </select>
        </div>

        {err && (
          <div data-testid="invite-error" style={{
            padding: 10, borderRadius: 6, background: '#FEE2E2',
            color: '#991B1B', fontSize: 13, marginBottom: 12,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            data-testid="invite-cancel-btn"
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: 600,
              background: 'white', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Cancel</button>
          <button
            onClick={send}
            disabled={!canSend}
            data-testid="invite-send-btn"
            style={{
              padding: '10px 22px', fontSize: 14, fontWeight: 700,
              background: canSend ? STUDY_THEME.gold : STUDY_THEME.line,
              color: canSend ? STUDY_THEME.ink : STUDY_THEME.muted,
              border: `1px solid ${canSend ? STUDY_THEME.goldDark : STUDY_THEME.line}`,
              borderRadius: 8, cursor: canSend ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {busy ? 'Sending…' : '🥊 Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
